import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import z from 'zod';
// Zod schemas for Google Calendar data types
export const googleCalendarDataSchema = z.object({
    calendarId: z.string().nullable().optional(),
    calendarName: z.string().nullable().optional(),
    syncToken: z.string().nullable().optional(),
    channelId: z.string().nullable().optional(),
    channelResourceId: z.string().nullable().optional(),
    channelExpiration: z.coerce.date().nullable().optional(),
    enabled: z.boolean().optional(),
    backgroundColor: z.string().nullable().optional(),
});
export const googleCalendarStatusEntrySchema = z.object({
    calendarId: z.string(),
    calendarName: z.string(),
    backgroundColor: z.string().nullable(),
    enabled: z.boolean(),
    lastSyncSuccessAt: z.coerce.date().nullable(),
    lastSyncError: z.string().nullable(),
    lastSyncErrorAt: z.coerce.date().nullable(),
});
export const googleCalendarStatusSchema = z.object({
    /** Credentials are stored -- not a promise that Google still accepts them */
    connected: z.boolean(),
    calendars: z.array(googleCalendarStatusEntrySchema),
    /** A pull or removal was requested recently and has not finished */
    syncInProgress: z.boolean(),
});
export const googleAuthUrlResponseSchema = z.object({
    authUrl: z.string().url(),
});
export const updateCalendarsRequestSchema = z.object({
    calendarId: z.string().min(1, 'Calendar ID is required'),
    calendarName: z.string().min(1, 'Calendar name is required'),
    enabled: z.boolean(),
    backgroundColor: z.string().optional(),
    deleteEvents: z.boolean().optional(),
});
/** Turns write on or off for one of the user's Zendia calendars */
export const writeCalendarRequestSchema = z.object({
    /** The Zendia calendar's id */
    calendarId: z.string().min(1, 'Calendar ID is required'),
    enabled: z.boolean(),
    /** When turning write off: also delete the Google calendar */
    deleteFromGoogle: z.boolean().optional(),
});
/** The body of `POST /google-calendar/calendars/update` */
export const updateGoogleCalendarsRequestSchema = z.object({
    /** The Google calendars to read into Zendia */
    calendars: z.array(updateCalendarsRequestSchema),
    /** The Zendia calendars to write to Google. Calendars not listed are left as they are. */
    writeCalendars: z.array(writeCalendarRequestSchema).optional(),
});
export const linkCalendarRequestSchema = z.object({
    calendarName: z.string().min(1, 'Calendar name is required'),
});
/** The response of `DELETE /google-calendar/connection` */
export const disconnectGoogleResponseSchema = z.object({
    /** The Zendia calendars whose Google calendar was to be deleted but is still in Google */
    undeletedWriteCalendars: z.array(z.string()),
});
// React Query hooks
/**
 * Hook to get Google OAuth2 authentication URL
 */
export const useGetGoogleAuthUrl = (authenticatedRequest) => useMutation({
    mutationFn: (params) => {
        const queryParams = new URLSearchParams();
        if (params.returnUrl)
            queryParams.append('returnUrl', params.returnUrl);
        if (params.state)
            queryParams.append('state', params.state);
        return authenticatedRequest({
            url: `/google-calendar/auth-url${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
            method: 'GET',
        }, googleAuthUrlResponseSchema);
    },
});
/**
 * Hook to get the list of Google Calendars for the authenticated user
 */
export const useGetGoogleCalendars = (authenticatedRequest, enabled = true) => useQuery({
    queryKey: ['googleCalendars'],
    queryFn: () => {
        return authenticatedRequest({
            url: '/google-calendar/calendars',
            method: 'GET',
        }, z.array(googleCalendarDataSchema));
    },
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
});
/**
 * Hook to get the user's Google Calendar connection status. Read from what Zendia has stored,
 * so it is cheap and never fails just because Google is unreachable.
 *
 * @param refetchInterval Poll while a sync runs, e.g. `(status) => status?.syncInProgress ? 5000 : false`
 */
export const useGetGoogleCalendarStatus = (authenticatedRequest, options = {}) => useQuery({
    queryKey: ['googleCalendarStatus'],
    queryFn: () => authenticatedRequest({
        url: '/google-calendar/status',
        method: 'GET',
    }, googleCalendarStatusSchema),
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval
        ? (query) => options.refetchInterval(query.state.data?.data)
        : undefined,
});
/**
 * Hook to update which calendars are read from Google and which Zendia calendars are written to it
 */
export const useUpdateGoogleCalendars = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request) => {
            const validatedData = updateGoogleCalendarsRequestSchema.parse(request);
            return authenticatedRequest({
                url: '/google-calendar/calendars/update',
                method: 'POST',
                data: validatedData,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['googleCalendars'] });
            queryClient.invalidateQueries({ queryKey: ['googleCalendarStatus'] });
            // Turning write on or off changes a Zendia calendar's sync data
            queryClient.invalidateQueries({ queryKey: ['calendars'] });
        },
    });
};
/**
 * Hook to link a specific calendar for synchronization
 */
export const useLinkGoogleCalendar = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ calendarId, calendarName }) => {
            const validatedCalendarId = z.string().min(1).parse(calendarId);
            const validatedData = linkCalendarRequestSchema.parse({ calendarName });
            return authenticatedRequest({
                url: `/google-calendar/calendars/${validatedCalendarId}/link`,
                method: 'POST',
                data: validatedData,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['googleCalendars'] });
        },
    });
};
/**
 * Hook to unlink a calendar from synchronization
 */
export const useUnlinkGoogleCalendar = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ calendarId, deleteEvents }) => {
            const validatedCalendarId = z.string().min(1).parse(calendarId);
            const queryParams = new URLSearchParams();
            if (deleteEvents)
                queryParams.append('delete', 'true');
            return authenticatedRequest({
                url: `/google-calendar/calendars/${validatedCalendarId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['googleCalendars'] });
        },
    });
};
/**
 * Hook to disconnect Google Calendar: stops all synchronization, revokes Zendia's access in the
 * user's Google account and forgets the stored credentials
 */
export const useDisconnectGoogleCalendar = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ deleteEvents, deleteWriteCalendars, }) => {
            const queryParams = new URLSearchParams();
            if (deleteEvents)
                queryParams.append('deleteEvents', 'true');
            if (deleteWriteCalendars)
                queryParams.append('deleteWriteCalendars', 'true');
            return authenticatedRequest({
                url: `/google-calendar/connection${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
                method: 'DELETE',
            }, disconnectGoogleResponseSchema);
        },
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: ['googleCalendars'] });
            queryClient.invalidateQueries({ queryKey: ['googleCalendarStatus'] });
            queryClient.invalidateQueries({ queryKey: ['calendars'] });
        },
    });
};
